import React from 'react';
import { CheckCircleFilled, CloseCircleFilled, DownOutlined, ToolOutlined } from '@ant-design/icons';
import styles from './ThinkingCard.less';

export interface ThinkingToolCall {
  id: string;
  name: string;
  costMs?: number;
}

interface Props {
  text: string;
  tools: ThinkingToolCall[];
  status: 'in-progress' | 'done' | 'failed';
  expanded: boolean;
  onToggle: () => void;
}

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

const formatDuration = (costMs?: number): string | null => {
  if (typeof costMs !== 'number' || costMs < 0) return null;
  return `${(costMs / 1000).toFixed(1)}s`;
};

const renderHeaderIcon = (status: Props['status']) => {
  if (status === 'done') {
    return <CheckCircleFilled className={styles.titleIcon} style={{ color: '#52c41a' }} />;
  }
  if (status === 'failed') {
    return <CloseCircleFilled className={styles.titleIcon} style={{ color: '#ff4d4f' }} />;
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" width={12} height={12}>
      <path d="M14 2L5 13.5h6.5L10 22l9.5-11H13L14 2Z" fill="#1677ff" />
    </svg>
  );
};

const renderHeaderTitle = (status: Props['status']) => {
  if (status === 'done') return '已完成分析';
  if (status === 'failed') return '分析已中止';
  return '正在分析';
};

export default function ThinkingCard({ text, tools, status, expanded, onToggle }: Props) {
  const inProgress = status === 'in-progress';

  return (
    <div className={cx(styles.card, status === 'failed' && styles.failed)}>
      <div className={styles.body}>
        <div className={styles.header} onClick={onToggle}>
          <div className={styles.title}>
            {renderHeaderIcon(status)}
            <span>{renderHeaderTitle(status)}</span>
          </div>
          <DownOutlined className={cx(styles.caret, expanded && styles.caretExpanded)} />
        </div>
        {expanded && (
          <div className={styles.content}>
            {tools.map((t) => {
              const duration = formatDuration(t.costMs);
              return (
                <div key={t.id} className={styles.toolRow}>
                  <ToolOutlined className={styles.toolIcon} />
                  <span>调用工具</span>
                  <span className={styles.toolDot}>·</span>
                  <span className={styles.toolName}>{t.name}</span>
                  {duration && (
                    <>
                      <span className={styles.toolDot}>·</span>
                      <span className={styles.toolDuration}>{duration}</span>
                    </>
                  )}
                </div>
              );
            })}
            {text && <div className={styles.processText}>{text}</div>}
            {inProgress && (
              <div className={styles.dotsRow}>
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** type=2 content.tools[] 元素映射为 ThinkingToolCall：name/costMs 字段名兼容多种命名 */
export const extractToolCalls = (rawTools: unknown): ThinkingToolCall[] => {
  if (!Array.isArray(rawTools)) return [];
  return rawTools.map((raw: any, idx: number) => {
    const name: string = raw?.name || raw?.toolName || raw?.tool || '未知工具';
    const costMs: number | undefined =
      typeof raw?.costMs === 'number'
        ? raw.costMs
        : typeof raw?.durationMs === 'number'
        ? raw.durationMs
        : typeof raw?.elapsedMs === 'number'
        ? raw.elapsedMs
        : undefined;
    const id: string = raw?.unique_id || raw?.id || `${name}_${idx}`;
    return { id, name, costMs };
  });
};
